import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

function SfmcExplorer() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [activeTab, setActiveTab] = useState('automations');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Data state
  const [automations, setAutomations] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [dataExtensions, setDataExtensions] = useState([]);
  const [maxDateCache, setMaxDateCache] = useState({});

  // Pagination state
  const [autoPage, setAutoPage] = useState(1);
  const [autoTotal, setAutoTotal] = useState(0);
  const [journeyPage, setJourneyPage] = useState(1);
  const [journeyTotal, setJourneyTotal] = useState(0);
  const [dePage, setDePage] = useState(1);
  const [deTotal, setDeTotal] = useState(0);

  // Sorting state
  const [autoSort, setAutoSort] = useState({ field: 'lastSaveDate', direction: 'desc' });
  const [journeySort, setJourneySort] = useState({ field: 'modifiedDate', direction: 'desc' });
  const [deSort, setDeSort] = useState({ field: 'modifiedDate', direction: 'desc' });

  // Quota and monitoring state
  const [quota, setQuota] = useState(null);
  const [monitoredConfigs, setMonitoredConfigs] = useState([]);
  const [actionLoading, setActionLoading] = useState('');

  // Preloading state for counts
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsLoaded, setCountsLoaded] = useState(false);

  const pageSize = 25;

  useEffect(() => {
    fetchAccounts();
    fetchQuota();
    fetchMonitoredConfigs();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      loadTabData();

      // Preload counts for tabs that haven't been loaded yet
      if (!countsLoaded) {
        preloadCounts();
      }
    }
  }, [selectedAccount, activeTab, autoPage, journeyPage, dePage]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/sfmc-accounts');
      setAccounts(response.data);
      if (response.data.length > 0) {
        setSelectedAccount(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load SFMC accounts');
      setTimeout(() => setError(''), 5000);
    }
  };

  const fetchQuota = async () => {
    try {
      const response = await axios.get('/api/dashboard/quota');
      setQuota(response.data);
    } catch (err) {
      console.error('Failed to fetch quota:', err);
    }
  };

  const fetchMonitoredConfigs = async () => {
    try {
      const response = await axios.get('/api/monitoring-configs');
      setMonitoredConfigs(response.data);
    } catch (err) {
      console.error('Failed to fetch monitored configs:', err);
    }
  };

  const preloadCounts = async () => {
    if (!selectedAccount || countsLoading || countsLoaded) return;

    setCountsLoading(true);

    try {
      // Load counts for tabs that haven't been visited yet
      const promises = [];

      if (journeyTotal === 0 && activeTab !== 'journeys') {
        promises.push(
          axios.get('/api/sfmc-objects/journeys', {
            params: { accountId: selectedAccount, page: 1, pageSize: 1 }
          }).then(response => {
            setJourneyTotal(response.data.total || 0);
          }).catch(() => {})
        );
      }

      if (deTotal === 0 && activeTab !== 'dataExtensions') {
        promises.push(
          axios.get('/api/sfmc-objects/data-extensions', {
            params: { accountId: selectedAccount, page: 1, pageSize: 1 }
          }).then(response => {
            setDeTotal(response.data.total || 0);
          }).catch(() => {})
        );
      }

      await Promise.all(promises);
      setCountsLoaded(true);
    } catch (err) {
      console.error('Failed to preload counts:', err);
    } finally {
      setCountsLoading(false);
    }
  };

  const isObjectMonitored = (objectId, objectType) => {
    return monitoredConfigs.some(config => config.object_id === objectId && config.object_type === objectType);
  };

  const getConfigForObject = (objectId, objectType) => {
    return monitoredConfigs.find(config => config.object_id === objectId && config.object_type === objectType);
  };

  const addToMonitoring = async (objectId, objectName, objectType) => {
    if (!quota?.canAdd) {
      setError(`Cannot add more objects. You've reached your ${quota?.plan} plan limit (${quota?.used}/${quota?.limit}).`);
      setTimeout(() => setError(''), 5000);
      return;
    }

    setActionLoading(objectId);
    setError('');
    setSuccessMessage('');

    try {
      await axios.post('/api/monitoring-configs', {
        sfmcAccountId: selectedAccount,
        configName: `${objectName} Monitor`,
        objectType,
        objectId,
        objectName,
        checkIntervalMinutes: 60,
        staleThresholdHours: 24,
        sendInstantAlerts: true,
        includeInDailyReport: true
      });

      setSuccessMessage(`Added "${objectName}" to monitoring`);
      fetchQuota();
      fetchMonitoredConfigs();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add to monitoring');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading('');
    }
  };

  const removeFromMonitoring = async (objectId, objectType) => {
    const config = getConfigForObject(objectId, objectType);
    if (!config) return;

    setActionLoading(objectId);
    setError('');
    setSuccessMessage('');

    try {
      await axios.delete(`/api/monitoring-configs/${config.id}`);
      setSuccessMessage(`Removed "${config.config_name}" from monitoring`);
      fetchQuota();
      fetchMonitoredConfigs();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove from monitoring');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading('');
    }
  };

  const loadTabData = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    setError('');

    try {
      if (activeTab === 'automations') {
        const response = await axios.get('/api/sfmc-objects/automations', {
          params: { accountId: selectedAccount, page: autoPage, pageSize }
        });
        setAutomations(response.data.automations || []);
        setAutoTotal(response.data.total || 0);
      } else if (activeTab === 'journeys') {
        const response = await axios.get('/api/sfmc-objects/journeys', {
          params: { accountId: selectedAccount, page: journeyPage, pageSize }
        });
        setJourneys(response.data.journeys || []);
        setJourneyTotal(response.data.total || 0);
      } else if (activeTab === 'dataExtensions') {
        const response = await axios.get('/api/sfmc-objects/data-extensions', {
          params: { accountId: selectedAccount, page: dePage, pageSize }
        });
        setDataExtensions(response.data.dataExtensions || []);
        setDeTotal(response.data.total || 0);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Fetch max date for a single DE
  const fetchMaxDate = async (customerKey) => {
    try {
      const response = await axios.get(`/api/sfmc-objects/data-extensions/${customerKey}/max-date`, {
        params: { accountId: selectedAccount }
      });
      
      if (response.data.success) {
        setMaxDateCache(prev => ({
          ...prev,
          [customerKey]: response.data.maxDate
        }));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to get max date';
      console.error(`Error fetching max date for ${customerKey}:`, errorMsg);
      setMaxDateCache(prev => ({
        ...prev,
        [customerKey]: `error: ${errorMsg}`
      }));
    }
  };

  // Max date fetching disabled - using modifiedDate instead
  // useEffect(() => {
  //   if (activeTab === 'dataExtensions' && dataExtensions.length > 0 && selectedAccount) {
  //     dataExtensions.forEach((de, index) => {
  //       if (maxDateCache[de.customerKey] === undefined) {
  //         setTimeout(() => { fetchMaxDate(de.customerKey); }, index * 2000);
  //       }
  //     });
  //   }
  // }, [dataExtensions, selectedAccount, activeTab]);


  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'active' || statusLower === 'running' || statusLower === 'published') {
      return '#10b981';
    }
    if (statusLower === 'paused' || statusLower === 'pausedschedule' || statusLower === 'draft') {
      return '#f59e0b';
    }
    if (statusLower === 'error' || statusLower === 'stopped' || statusLower === 'inactive') {
      return '#ef4444';
    }
    return '#6b7280';
  };

  // Sorting functions - monitored items always come first
  const sortData = (data, sortConfig, objectType, idField = 'id') => {
    return [...data].sort((a, b) => {
      // First, prioritize monitored items at the top
      const aMonitored = isObjectMonitored(a[idField], objectType);
      const bMonitored = isObjectMonitored(b[idField], objectType);

      if (aMonitored && !bMonitored) return -1;
      if (!aMonitored && bMonitored) return 1;

      // If both have same monitoring status, apply regular sort
      if (!sortConfig.field) return 0;

      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];

      // Handle dates
      if (sortConfig.field.toLowerCase().includes('date') || sortConfig.field.toLowerCase().includes('time')) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle numbers
      else if (typeof aVal === 'number' && typeof bVal === 'number') {
        // Keep as numbers
      }
      // Handle strings
      else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Memoized sorted data - recalculates when data, sort config, or monitored configs change
  const sortedAutomations = useMemo(() =>
    sortData(automations, autoSort, 'automation', 'id'),
    [automations, autoSort, monitoredConfigs]
  );

  const sortedJourneys = useMemo(() =>
    sortData(journeys, journeySort, 'journey', 'id'),
    [journeys, journeySort, monitoredConfigs]
  );

  const sortedDataExtensions = useMemo(() =>
    sortData(dataExtensions, deSort, 'data_extension', 'customerKey'),
    [dataExtensions, deSort, monitoredConfigs]
  );

  const handleSort = (setSort, currentSort, field) => {
    const newDirection = currentSort.field === field && currentSort.direction === 'desc' ? 'asc' : 'desc';
    setSort({ field, direction: newDirection });
  };

  const SortArrow = ({ field, sortConfig }) => {
    if (sortConfig.field !== field) {
      return <span style={{ color: '#d1d5db', marginLeft: '5px' }}>↕</span>;
    }
    return (
      <span style={{ marginLeft: '5px', color: '#3b82f6' }}>
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const SortableHeader = ({ field, label, sortConfig, onSort, align = 'left' }) => (
    <th
      style={{
        textAlign: align,
        padding: '12px 8px',
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap'
      }}
      onClick={() => onSort(field)}
    >
      {label}
      <SortArrow field={field} sortConfig={sortConfig} />
    </th>
  );

  const renderPagination = (currentPage, totalItems, setPage) => {
    const totalPages = Math.ceil(totalItems / pageSize);
    if (totalPages <= 1) return null;

    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
        <button
          className="btn btn-secondary"
          onClick={() => setPage(currentPage - 1)}
          disabled={currentPage <= 1 || loading}
          style={{ padding: '6px 12px' }}
        >
          Previous
        </button>
        <span style={{ padding: '6px 12px', background: '#f3f4f6', borderRadius: '6px' }}>
          Page {currentPage} of {totalPages} ({totalItems} total)
        </span>
        <button
          className="btn btn-secondary"
          onClick={() => setPage(currentPage + 1)}
          disabled={currentPage >= totalPages || loading}
          style={{ padding: '6px 12px' }}
        >
          Next
        </button>
      </div>
    );
  };

  const renderAutomationsTab = () => {
    return (
      <div>
        {automations.length === 0 && !loading ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>
            No automations found
          </p>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <SortableHeader
                    field="name"
                    label="Name"
                    sortConfig={autoSort}
                    onSort={(f) => handleSort(setAutoSort, autoSort, f)}
                  />
                  <SortableHeader
                    field="status"
                    label="Status"
                    sortConfig={autoSort}
                    onSort={(f) => handleSort(setAutoSort, autoSort, f)}
                  />
                  <SortableHeader
                    field="lastRunTime"
                    label="Last Run"
                    sortConfig={autoSort}
                    onSort={(f) => handleSort(setAutoSort, autoSort, f)}
                  />
                  <SortableHeader
                    field="lastSaveDate"
                    label="Last Modified"
                    sortConfig={autoSort}
                    onSort={(f) => handleSort(setAutoSort, autoSort, f)}
                  />
                  <SortableHeader
                    field="type"
                    label="Type"
                    sortConfig={autoSort}
                    onSort={(f) => handleSort(setAutoSort, autoSort, f)}
                  />
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAutomations.map((auto) => (
                  <tr key={auto.id} style={{
                    borderBottom: '1px solid #e5e7eb',
                    background: isObjectMonitored(auto.id, 'automation') ? '#f0fdf4' : 'transparent'
                  }}>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: '500' }}>
                        {isObjectMonitored(auto.id, 'automation') && (
                          <span style={{ color: '#16a34a', marginRight: '6px' }}>●</span>
                        )}
                        {auto.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{auto.id}</div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'white',
                        background: getStatusColor(auto.status)
                      }}>
                        {auto.status || 'Unknown'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {formatDate(auto.lastRunTime)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {formatDate(auto.lastSaveDate)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {auto.type || 'Scheduled'}
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      {isObjectMonitored(auto.id, 'automation') ? (
                        <button
                          onClick={() => removeFromMonitoring(auto.id, 'automation')}
                          disabled={actionLoading === auto.id}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: '#fee2e2',
                            color: '#991b1b',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            cursor: actionLoading === auto.id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {actionLoading === auto.id ? '...' : 'Stop Monitoring'}
                        </button>
                      ) : (
                        <button
                          onClick={() => addToMonitoring(auto.id, auto.name, 'automation')}
                          disabled={actionLoading === auto.id || !quota?.canAdd}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: quota?.canAdd ? '#dcfce7' : '#f3f4f6',
                            color: quota?.canAdd ? '#166534' : '#9ca3af',
                            border: `1px solid ${quota?.canAdd ? '#bbf7d0' : '#d1d5db'}`,
                            borderRadius: '4px',
                            cursor: (actionLoading === auto.id || !quota?.canAdd) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {actionLoading === auto.id ? '...' : 'Monitor'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderPagination(autoPage, autoTotal, setAutoPage)}
          </>
        )}
      </div>
    );
  };

  const renderJourneysTab = () => {
    const regularJourneys = sortedJourneys.filter(j => j.definitionType !== 'SingleSend');
    const ssoJourneys = sortedJourneys.filter(j => j.definitionType === 'SingleSend');

    return (
      <div>
        {journeys.length === 0 && !loading ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>
            No journeys found
          </p>
        ) : (
          <>
            <h4 style={{ marginBottom: '15px' }}>Regular Journeys ({regularJourneys.length})</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <SortableHeader
                    field="name"
                    label="Name"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <SortableHeader
                    field="status"
                    label="Status"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <SortableHeader
                    field="version"
                    label="Version"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <SortableHeader
                    field="modifiedDate"
                    label="Modified"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {regularJourneys.map((journey) => (
                  <tr key={journey.id} style={{
                    borderBottom: '1px solid #e5e7eb',
                    background: isObjectMonitored(journey.id, 'journey') ? '#f0fdf4' : 'transparent'
                  }}>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: '500' }}>
                        {isObjectMonitored(journey.id, 'journey') && (
                          <span style={{ color: '#16a34a', marginRight: '6px' }}>●</span>
                        )}
                        {journey.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{journey.id}</div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'white',
                        background: getStatusColor(journey.status)
                      }}>
                        {journey.status || 'Unknown'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {journey.version || 1}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {formatDate(journey.modifiedDate)}
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      {isObjectMonitored(journey.id, 'journey') ? (
                        <button
                          onClick={() => removeFromMonitoring(journey.id, 'journey')}
                          disabled={actionLoading === journey.id}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: '#fee2e2',
                            color: '#991b1b',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            cursor: actionLoading === journey.id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {actionLoading === journey.id ? '...' : 'Stop Monitoring'}
                        </button>
                      ) : (
                        <button
                          onClick={() => addToMonitoring(journey.id, journey.name, 'journey')}
                          disabled={actionLoading === journey.id || !quota?.canAdd}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: quota?.canAdd ? '#dcfce7' : '#f3f4f6',
                            color: quota?.canAdd ? '#166534' : '#9ca3af',
                            border: `1px solid ${quota?.canAdd ? '#bbf7d0' : '#d1d5db'}`,
                            borderRadius: '4px',
                            cursor: (actionLoading === journey.id || !quota?.canAdd) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {actionLoading === journey.id ? '...' : 'Monitor'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 style={{ marginBottom: '15px' }}>Single Send Journeys ({ssoJourneys.length})</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <SortableHeader
                    field="name"
                    label="Name"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <SortableHeader
                    field="status"
                    label="Status"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <SortableHeader
                    field="version"
                    label="Version"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <SortableHeader
                    field="modifiedDate"
                    label="Modified"
                    sortConfig={journeySort}
                    onSort={(f) => handleSort(setJourneySort, journeySort, f)}
                  />
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ssoJourneys.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      No single send journeys found
                    </td>
                  </tr>
                ) : (
                  ssoJourneys.map((journey) => (
                    <tr key={journey.id} style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: isObjectMonitored(journey.id, 'journey') ? '#f0fdf4' : 'transparent'
                    }}>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ fontWeight: '500' }}>
                          {isObjectMonitored(journey.id, 'journey') && (
                            <span style={{ color: '#16a34a', marginRight: '6px' }}>●</span>
                          )}
                          {journey.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{journey.id}</div>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'white',
                          background: getStatusColor(journey.status)
                        }}>
                          {journey.status || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        {journey.version || 1}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        {formatDate(journey.modifiedDate)}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        {isObjectMonitored(journey.id, 'journey') ? (
                          <button
                            onClick={() => removeFromMonitoring(journey.id, 'journey')}
                            disabled={actionLoading === journey.id}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              background: '#fee2e2',
                              color: '#991b1b',
                              border: '1px solid #fecaca',
                              borderRadius: '4px',
                              cursor: actionLoading === journey.id ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {actionLoading === journey.id ? '...' : 'Stop Monitoring'}
                          </button>
                        ) : (
                          <button
                            onClick={() => addToMonitoring(journey.id, journey.name, 'journey')}
                            disabled={actionLoading === journey.id || !quota?.canAdd}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              background: quota?.canAdd ? '#dcfce7' : '#f3f4f6',
                              color: quota?.canAdd ? '#166534' : '#9ca3af',
                              border: `1px solid ${quota?.canAdd ? '#bbf7d0' : '#d1d5db'}`,
                              borderRadius: '4px',
                              cursor: (actionLoading === journey.id || !quota?.canAdd) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {actionLoading === journey.id ? '...' : 'Monitor'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {renderPagination(journeyPage, journeyTotal, setJourneyPage)}
          </>
        )}
      </div>
    );
  };

  const renderDataExtensionsTab = () => {
    return (
      <div>
        {dataExtensions.length === 0 && !loading ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>
            No data extensions found
          </p>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <SortableHeader
                    field="name"
                    label="Name"
                    sortConfig={deSort}
                    onSort={(f) => handleSort(setDeSort, deSort, f)}
                  />
                  <SortableHeader
                    field="rowCount"
                    label="Row Count"
                    sortConfig={deSort}
                    onSort={(f) => handleSort(setDeSort, deSort, f)}
                    align="right"
                  />
                  <SortableHeader
                    field="modifiedDate"
                    label="Last Modified"
                    sortConfig={deSort}
                    onSort={(f) => handleSort(setDeSort, deSort, f)}
                  />
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedDataExtensions.map((de) => (
                  <tr key={de.customerKey} style={{
                    borderBottom: '1px solid #e5e7eb',
                    background: isObjectMonitored(de.customerKey, 'data_extension') ? '#f0fdf4' : 'transparent'
                  }}>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: '500' }}>
                        {isObjectMonitored(de.customerKey, 'data_extension') && (
                          <span style={{ color: '#16a34a', marginRight: '6px' }}>●</span>
                        )}
                        {de.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{de.customerKey}</div>
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '500' }}>
                      {de.rowCount.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {formatDate(de.modifiedDate)}
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      {isObjectMonitored(de.customerKey, 'data_extension') ? (
                        <button
                          onClick={() => removeFromMonitoring(de.customerKey, 'data_extension')}
                          disabled={actionLoading === de.customerKey}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: '#fee2e2',
                            color: '#991b1b',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            cursor: actionLoading === de.customerKey ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {actionLoading === de.customerKey ? '...' : 'Stop Monitoring'}
                        </button>
                      ) : (
                        <button
                          onClick={() => addToMonitoring(de.customerKey, de.name, 'data_extension')}
                          disabled={actionLoading === de.customerKey || !quota?.canAdd}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            background: quota?.canAdd ? '#dcfce7' : '#f3f4f6',
                            color: quota?.canAdd ? '#166534' : '#9ca3af',
                            border: `1px solid ${quota?.canAdd ? '#bbf7d0' : '#d1d5db'}`,
                            borderRadius: '4px',
                            cursor: (actionLoading === de.customerKey || !quota?.canAdd) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {actionLoading === de.customerKey ? '...' : 'Monitor'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderPagination(dePage, deTotal, setDePage)}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="content-header">
        <div>
          <h1 style={{ margin: '0 0 5px 0' }}>SFMC Explorer</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Browse all automations, journeys, and data extensions in your SFMC account
          </p>
        </div>
      </div>

      <div className="content-body">
        {/* Floating Notification Panel */}
        {(error || successMessage) && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            minWidth: '300px',
            maxWidth: '500px',
            animation: 'slideIn 0.3s ease-out'
          }}>
            {error && (
              <div style={{
                padding: '12px 16px',
                background: '#fecaca',
                color: '#991b1b',
                borderRadius: '8px',
                marginBottom: '10px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{error}</span>
                <button
                  onClick={() => setError('')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#991b1b',
                    cursor: 'pointer',
                    fontSize: '20px',
                    padding: '0 5px',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
              </div>
            )}
            {successMessage && (
              <div style={{
                padding: '12px 16px',
                background: '#dcfce7',
                color: '#166534',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{successMessage}</span>
                <button
                  onClick={() => setSuccessMessage('')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#166534',
                    cursor: 'pointer',
                    fontSize: '20px',
                    padding: '0 5px',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        )}

        {/* Quota Display */}
        {quota && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-body" style={{ padding: '15px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '5px' }}>
                    Monitoring Quota
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                    {quota.plan.charAt(0).toUpperCase() + quota.plan.slice(1)} Plan
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: quota.canAdd ? '#166534' : '#991b1b' }}>
                    {quota.used} / {quota.limit}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                    {quota.remaining} slots remaining
                  </div>
                </div>
                <div style={{ width: '200px' }}>
                  <div style={{
                    height: '8px',
                    background: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(quota.used / quota.limit) * 100}%`,
                      background: quota.used >= quota.limit ? '#dc2626' : quota.used >= quota.limit * 0.8 ? '#f59e0b' : '#10b981',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Account Selector */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <label style={{ fontWeight: '500' }}>SFMC Account:</label>
              <select
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  setCountsLoaded(false); // Reset counts when account changes
                }}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', minWidth: '250px' }}
              >
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.account_name}
                  </option>
                ))}
              </select>
              {loading && (
                <span style={{ color: '#6b7280' }}>Loading...</span>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card">
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #e5e7eb',
            background: '#f9fafb',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px'
          }}>
            <button
              onClick={() => setActiveTab('automations')}
              style={{
                padding: '12px 24px',
                background: activeTab === 'automations' ? 'white' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'automations' ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: '-2px',
                cursor: 'pointer',
                fontWeight: activeTab === 'automations' ? '600' : '400',
                color: activeTab === 'automations' ? '#3b82f6' : '#6b7280'
              }}
            >
              Automations ({autoTotal})
            </button>
            <button
              onClick={() => setActiveTab('journeys')}
              style={{
                padding: '12px 24px',
                background: activeTab === 'journeys' ? 'white' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'journeys' ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: '-2px',
                cursor: 'pointer',
                fontWeight: activeTab === 'journeys' ? '600' : '400',
                color: activeTab === 'journeys' ? '#3b82f6' : '#6b7280'
              }}
            >
              Journeys ({journeyTotal})
            </button>
            <button
              onClick={() => setActiveTab('dataExtensions')}
              style={{
                padding: '12px 24px',
                background: activeTab === 'dataExtensions' ? 'white' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'dataExtensions' ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: '-2px',
                cursor: 'pointer',
                fontWeight: activeTab === 'dataExtensions' ? '600' : '400',
                color: activeTab === 'dataExtensions' ? '#3b82f6' : '#6b7280'
              }}
            >
              Data Extensions ({deTotal})
            </button>
          </div>
          <div className="card-body">
            {activeTab === 'automations' && renderAutomationsTab()}
            {activeTab === 'journeys' && renderJourneysTab()}
            {activeTab === 'dataExtensions' && renderDataExtensionsTab()}
          </div>
        </div>
      </div>

      {/* Add animation keyframes to App.css */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default SfmcExplorer;
